import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { imageurl } from "../../App";
import { brandLogo } from "../../assets/img/logo/brandLogos";
import { loadStoreSettings } from "../../util/storeSettings";
import { configureMetaPixel, trackMetaPurchaseOnce } from "../../util/metaPixel";
import PostPurchaseOffer from "./PostPurchaseOffer";
import {
  FaCheckCircle,
  FaShoppingBag,
  FaTruck,
  FaMapMarkerAlt,
  FaArrowRight,
  FaPhoneAlt,
  FaEnvelope,
  FaFileInvoiceDollar
} from "react-icons/fa";

export default function OrderSuccess() {
  const { orderId } = useParams();
  const [orderDetails, setOrderDetails] = useState(null);
  const [storeSettings, setStoreSettings] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);

    loadStoreSettings().then((settings) => {
      if (settings && (settings.storeName || settings.logoUrl)) {
        setStoreSettings(settings);
        document.title = `Order Confirmed #${orderId} — ${settings.storeName || "Store"}`;
      }
    }).catch(() => {
      document.title = `Order Confirmed #${orderId}`;
    });

    try {
      const raw = sessionStorage.getItem("last_order_details");
      if (raw) {
        setOrderDetails(JSON.parse(raw));
      }
    } catch {
      // ignore
    }
  }, [orderId]);

  useEffect(() => {
    if (!orderDetails || !storeSettings) return;
    configureMetaPixel().then((configured) => {
      if (!configured) return;
      const items = Array.isArray(orderDetails.items) ? orderDetails.items : [];
      trackMetaPurchaseOnce(orderId, {
        content_ids: items.map((item) => String(item.product_id || item.id).split(":")[0]),
        content_type: "product",
        value: Number(orderDetails.totalAmount || 0),
        currency: storeSettings.currency || "PKR",
        num_items: items.reduce((sum, item) => sum + Number(item.qty || 0), 0),
        contents: items.map((item) => ({
          id: String(item.product_id || item.id).split(":")[0],
          quantity: Number(item.qty || 1),
        })),
      });
    }).catch(() => {});
  }, [orderDetails, storeSettings, orderId]);

  // Keep the receipt in step with the order after a post-purchase add-on.
  const addOfferItem = (item, grandTotal) => {
    setOrderDetails((current) => {
      if (!current) return current;
      const next = { ...current, items: [...(current.items || []), item], totalAmount: grandTotal };
      try {
        sessionStorage.setItem("last_order_details", JSON.stringify(next));
      } catch {
        // ignore
      }
      return next;
    });
  };

  const storeName = storeSettings?.storeName || "Store";
  const currencySymbol = storeSettings?.currencySymbol || "Rs.";
  const storeLogo = storeSettings?.logoUrl
    ? (storeSettings.logoUrl.startsWith("http")
        ? storeSettings.logoUrl
        : `${imageurl}/${storeSettings.logoUrl.replace(/^\/+/, "")}`)
    : brandLogo;

  return (
    <div className="py-5" style={{ backgroundColor: "#f8fafc", minHeight: "85vh" }}>
      <div className="container" style={{ maxWidth: "780px" }}>
        {/* Top Success Banner Card */}
        <div className="bg-white p-4 p-md-5 rounded-4 border text-center mb-4" style={{ boxShadow: "var(--shadow-sm)" }}>
          <div className="mb-4">
            <img
              src={storeLogo}
              alt={storeName}
              style={{ maxHeight: "48px", maxWidth: "200px", objectFit: "contain" }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = brandLogo;
              }}
            />
          </div>

          <div
            className="mx-auto mb-3 d-flex align-items-center justify-content-center rounded-circle"
            style={{ width: "72px", height: "72px", background: "#dcfce7", color: "#16a34a" }}
          >
            <FaCheckCircle size={36} />
          </div>

          <span className="badge bg-success-subtle text-success px-3 py-2 rounded-pill fw-bold mb-2">
            Order Confirmed 🎉
          </span>

          <h2 className="fw-extrabold mb-2" style={{ color: "#0f172a", letterSpacing: "-0.5px" }}>
            Thank You For Your Order!
          </h2>
          <p className="text-muted mb-4" style={{ fontSize: "14.5px" }}>
            {orderDetails?.paymentSlipPending
              ? "We received your payment photo. This order stays pending until the store accepts it."
              : "We've received your order and our fulfillment team is preparing it for shipment."}
          </p>

          <div className="p-3 bg-light rounded-3 d-flex justify-content-center align-items-center gap-4 flex-wrap">
            <div>
              <span className="text-muted" style={{ fontSize: "12px" }}>Order Reference:</span>
              <h5 className="fw-bold mb-0 text-dark">#ORD-{orderId}</h5>
            </div>
            {orderDetails?.trackingNo && (
              <div>
                <span className="text-muted" style={{ fontSize: "12px" }}>Tracking Number:</span>
                <h5 className="fw-bold mb-0 text-primary">{orderDetails.trackingNo}</h5>
              </div>
            )}
          </div>
        </div>

        {orderDetails?.trackingNo && (
          <PostPurchaseOffer
            orderId={orderId}
            trackingNo={orderDetails.trackingNo}
            currencySymbol={currencySymbol}
            onAdded={addOfferItem}
          />
        )}

        {/* Receipt & Details */}
        {orderDetails && (
          <div className="bg-white p-4 p-md-5 rounded-4 border mb-4" style={{ boxShadow: "var(--shadow-sm)" }}>
            <h5 className="fw-bold mb-4 pb-2 border-bottom">Order Summary</h5>

            {/* Items */}
            <div className="d-flex flex-column gap-3 mb-4">
              {orderDetails.items?.map((item, idx) => (
                <div key={idx} className="d-flex align-items-center justify-content-between gap-3">
                  <div>
                    <h6 className="fw-bold mb-0" style={{ fontSize: "14px" }}>
                      {item.name}
                    </h6>
                    <span className="text-muted" style={{ fontSize: "12.5px" }}>
                      Qty: {item.qty} × {currencySymbol} {Number(item.price).toLocaleString()}
                    </span>
                  </div>
                  <span className="fw-bold" style={{ fontSize: "14.5px" }}>
                    {currencySymbol} {Number(item.price * item.qty).toLocaleString()}
                  </span>
                </div>
              ))}
            </div>

            <hr />

            {/* Total */}
            <div className="d-flex justify-content-between align-items-center py-2">
              <span className="fw-bold" style={{ fontSize: "16px" }}>Total Amount ({orderDetails.paymentMethod})</span>
              <span className="fw-extrabold" style={{ fontSize: "20px", color: "var(--accent)" }}>
                {currencySymbol} {Number(orderDetails.totalAmount).toLocaleString()}
              </span>
            </div>

            <hr />

            {/* Delivery Info */}
            <div className="row g-3 pt-2">
              <div className="col-12 col-md-6">
                <span className="text-muted fw-semibold" style={{ fontSize: "12px" }}>SHIPPING TO</span>
                <p className="fw-bold mb-1" style={{ fontSize: "14px" }}>{orderDetails.customerName}</p>
                <p className="text-muted mb-0" style={{ fontSize: "13px" }}>
                  <FaMapMarkerAlt className="me-1 text-danger" /> {orderDetails.address}
                </p>
              </div>
              <div className="col-12 col-md-6">
                <span className="text-muted fw-semibold" style={{ fontSize: "12px" }}>CONTACT & DELIVERY</span>
                <p className="text-muted mb-1" style={{ fontSize: "13px" }}>
                  <FaPhoneAlt className="me-1" /> {orderDetails.phone}
                </p>
                <p className="text-muted mb-0" style={{ fontSize: "13px" }}>
                  <FaEnvelope className="me-1" /> {orderDetails.email}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="d-flex align-items-center justify-content-center gap-3 flex-wrap">
          <Link
            to={`/order-invoice/${orderId}${orderDetails?.trackingNo ? `?tracking=${encodeURIComponent(orderDetails.trackingNo)}` : ""}`}
            className="btn btn-primary rounded-pill px-4 py-3 fw-bold d-flex align-items-center gap-2 shadow-sm"
          >
            <FaFileInvoiceDollar size={15} /> View & print invoice
          </Link>

          <Link
            to={`/track-order?tracking=${orderDetails?.trackingNo || orderId}`}
            className="btn btn-dark rounded-pill px-4 py-3 fw-bold d-flex align-items-center gap-2 shadow-sm"
          >
            <FaTruck size={14} /> Track Order Status
          </Link>

          <Link
            to="/collection"
            className="btn btn-outline-secondary rounded-pill px-4 py-3 fw-semibold d-flex align-items-center gap-2"
          >
            <FaShoppingBag size={14} /> Continue Shopping <FaArrowRight size={12} />
          </Link>
        </div>
      </div>
    </div>
  );
}
